import {
  Box,
  Button,
  DatePicker,
  HorizontalGrid,
  HorizontalStack,
  Icon,
  OptionList,
  Popover,
  Scrollable,
  Select,
  TextField,
  VerticalStack,
  useBreakpoints,
} from '@shopify/polaris';
import { CalendarMinor } from '@shopify/polaris-icons';
import { useMemo, useState } from 'react';
import { useTimezone } from '../../../customHooks/useTimezone';
import { todayInTz, toYMD } from '../../../helpers/dates';

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

// `YYYY-MM-DD` → Date con componentes locales (lo que espera el DatePicker
// de Polaris). Nunca `new Date('YYYY-MM-DD')`, que lo lee como UTC.
const ymdToLocalDate = (ymd) => {
  if (!ymd || !YMD_RE.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const displayYmd = (ymd) => (ymd ? ymd.split('-').reverse().join('/') : '');

/**
 * Selector de UNA fecha con atajos. Hermano de `DateRangePicker` (el de
 * `/reports/invoiced`): atajos a la izquierda, campo y calendario a la
 * derecha, Cancelar/Aplicar abajo. Controlado: recibe `value`
 * (`YYYY-MM-DD` o `''`) y emite lo mismo al aplicar, así el padre decide
 * cuándo relanzar la consulta (una vez, no por cada clic del calendario).
 *
 * `presets`: [{ key, label, value }] con `value` ya calculado. Un preset con
 * `value: ''` representa "sin fecha" (p.ej. saldo actual).
 */
const DatePresetPicker = ({ value, onChange, presets = [], disabled = false, label = 'Fecha' }) => {
  const { mdDown, lgUp } = useBreakpoints();
  const { timezone } = useTimezone();
  const today = useMemo(() => todayInTz(timezone), [timezone]);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [typed, setTyped] = useState('');
  // Con dos meses a la vista, el elegido va a la derecha: el de la izquierda
  // es el anterior (el de después casi siempre es futuro y está deshabilitado).
  const monthFor = (d) => {
    const shown = lgUp ? new Date(d.getFullYear(), d.getMonth() - 1, 1) : d;
    return { month: shown.getMonth(), year: shown.getFullYear() };
  };
  const [{ month, year }, setMonthYear] = useState(() => monthFor(ymdToLocalDate(value) || today));

  const openPopover = () => {
    setDraft(value || '');
    setTyped(value || '');
    setMonthYear(monthFor(ymdToLocalDate(value) || today));
    setOpen(true);
  };

  const presetFor = (ymd) => presets.find((p) => p.value === ymd);
  const draftDate = ymdToLocalDate(draft) || (draft === '' ? today : null);

  const pick = (ymd, { keepMonth = false } = {}) => {
    setDraft(ymd);
    setTyped(ymd);
    if (!keepMonth) setMonthYear(monthFor(ymdToLocalDate(ymd) || today));
  };

  const onTyped = (v) => {
    setTyped(v);
    if (YMD_RE.test(v) && ymdToLocalDate(v) <= today) pick(v);
  };

  const apply = () => {
    setOpen(false);
    if (draft !== (value || '')) onChange(draft);
  };

  const activePreset = presetFor(value || '');
  const buttonLabel = activePreset ? activePreset.label : displayYmd(value);
  const selectedPreset = presetFor(draft)?.key;

  return (
    <Popover
      active={open}
      autofocusTarget="none"
      preferredAlignment="left"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <Button icon={CalendarMinor} onClick={() => (open ? setOpen(false) : openPopover())} disabled={disabled}>
          {buttonLabel || label}
        </Button>
      }
      onClose={() => setOpen(false)}
    >
      <Popover.Pane fixed>
        <HorizontalGrid columns={{ xs: '1fr', mdDown: '1fr', md: 'max-content max-content' }} gap={0}>
          {presets.length > 0 && (
            <Box
              maxWidth={mdDown ? '516px' : '232px'}
              width={mdDown ? '100%' : '232px'}
              padding={{ xs: 5, md: 0 }}
              paddingBlockEnd={{ xs: 1, md: 0 }}
            >
              {mdDown ? (
                <Select
                  label="Atajos"
                  labelHidden
                  options={[{ label: 'Personalizada', value: '' }, ...presets.map((p) => ({ label: p.label, value: p.key }))]}
                  value={selectedPreset || ''}
                  onChange={(k) => {
                    const p = presets.find((x) => x.key === k);
                    if (p) pick(p.value);
                  }}
                />
              ) : (
                <Scrollable style={{ height: '334px' }}>
                  <OptionList
                    options={presets.map((p) => ({ value: p.key, label: p.label }))}
                    selected={selectedPreset ? [selectedPreset] : []}
                    onChange={([k]) => {
                      const p = presets.find((x) => x.key === k);
                      if (p) pick(p.value);
                    }}
                  />
                </Scrollable>
              )}
            </Box>
          )}
          <Box padding={{ xs: 5 }} maxWidth={mdDown ? '320px' : '516px'}>
            <VerticalStack gap="4">
              <TextField
                label={label}
                labelHidden
                prefix={<Icon source={CalendarMinor} />}
                placeholder="AAAA-MM-DD"
                value={typed}
                onChange={onTyped}
                autoComplete="off"
              />
              <DatePicker
                month={month}
                year={year}
                selected={draftDate || undefined}
                onMonthChange={(m, y) => setMonthYear({ month: m, year: y })}
                onChange={({ start }) => pick(toYMD(start), { keepMonth: true })}
                multiMonth={lgUp}
                weekStartsOn={1}
                disableDatesAfter={today}
              />
            </VerticalStack>
          </Box>
        </HorizontalGrid>
      </Popover.Pane>
      <Popover.Pane fixed>
        <Popover.Section>
          <HorizontalStack align="end" gap="2">
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
            <Button primary onClick={apply}>
              Aplicar
            </Button>
          </HorizontalStack>
        </Popover.Section>
      </Popover.Pane>
    </Popover>
  );
};

export default DatePresetPicker;
